/**
 * Shared machinery for receipt-text parsers.
 *
 * Two rules govern everything in this folder:
 *   1. A field that is not in the text is not in the result. Nothing is
 *      guessed, defaulted or invented.
 *   2. Every field that *is* extracted carries a confidence score, and a
 *      low-confidence result lands in the review inbox rather than the ledger.
 */
import { parseAmountToSen } from '../money.js';
import { zonedTimeToUtc, DEFAULT_TIME_ZONE } from '../time.js';
import type { TransactionType } from '../schemas.js';

/** One extracted value plus how much we trust it and where it came from. */
export interface Extracted<T> {
  value: T;
  confidence: number;
  /** The exact line the value was read from, shown in the review screen. */
  evidence: string;
}

export interface ParsedReceipt {
  /** Which parser produced this. */
  parser: 'maybank' | 'tng' | 'generic';
  /** Amount is the only mandatory field; without it there is no transaction. */
  amountSen: Extracted<number>;
  merchant?: Extracted<string>;
  occurredAt?: Extracted<string>;
  reference?: Extracted<string>;
  type?: Extracted<TransactionType>;
  currency: string;
  /** Aggregate confidence, used to decide whether this needs review. */
  confidence: number;
  /** Human-readable notes about what could not be read. */
  warnings: string[];
}

export interface ParseFailure {
  ok: false;
  parser: string;
  reason: string;
}

export type ParseResult = ({ ok: true } & ParsedReceipt) | ParseFailure;

export interface ParseOptions {
  timeZone?: string;
  /** Used only to resolve a receipt that gives a day and month but no year. */
  now?: Date;
}

/** Normalise the mess that OCR and clipboard copies produce. */
export function normalizeReceiptText(raw: string): string[] {
  return raw
    .normalize('NFKC')
    .replace(/\r\n?/g, '\n')
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line) => line.length > 0);
}

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
  /** True when the text carried a clock time as well as a date. */
  hasTime: boolean;
}

function applyMeridiem(hour: number, meridiem: string | undefined): number {
  if (!meridiem) return hour;
  const marker = meridiem.toLowerCase().replace(/[^apm]/g, '');
  if (marker === 'pm') return hour === 12 ? 12 : hour + 12;
  if (marker === 'am') return hour === 12 ? 0 : hour;
  return hour;
}

function readTime(text: string): { hour: number; minute: number; second: number } | undefined {
  const match = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp]\.?[Mm]\.?)?/.exec(text);
  if (!match) return undefined;
  const hour = applyMeridiem(Number(match[1]), match[4]);
  const minute = Number(match[2]);
  const second = Number(match[3] ?? 0);
  if (hour > 23 || minute > 59 || second > 59) return undefined;
  return { hour, minute, second };
}

/**
 * Read a date out of a line of receipt text. Returns undefined rather than a
 * guess when the line does not clearly contain one.
 *
 * Understood: `17 May 2024, 08:42 PM`, `17/05/2024 20:42`, `17-05-2024`,
 * `2024-05-17 20:42:11`, `May 17, 2024`.
 * Day-first is assumed for ambiguous slash dates, which is the Malaysian
 * convention; an unambiguous day (>12) confirms it and raises confidence.
 */
export function parseReceiptDate(
  text: string,
  options: ParseOptions = {},
): { parts: DateParts; confidence: number } | undefined {
  const now = options.now ?? new Date();
  const time = readTime(text);

  // 2024-05-17 (ISO, unambiguous)
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) {
    return {
      parts: {
        year: Number(iso[1]),
        month: Number(iso[2]),
        day: Number(iso[3]),
        ...time,
        hasTime: Boolean(time),
      },
      confidence: time ? 0.97 : 0.85,
    };
  }

  // 17 May 2024 / 17 May 24 / 17 May
  const dayMonth = new RegExp(
    String.raw`\b(\d{1,2})\s*(?:st|nd|rd|th)?[\s.-]+([A-Za-z]{3,9})\.?(?:[\s,.-]+(\d{2,4}))?\b`,
  ).exec(text);
  if (dayMonth) {
    const month = MONTHS[(dayMonth[2] ?? '').toLowerCase()];
    if (month) {
      const day = Number(dayMonth[1]);
      const yearText = dayMonth[3];
      const year = yearText
        ? yearText.length === 2
          ? 2000 + Number(yearText)
          : Number(yearText)
        : now.getUTCFullYear();
      if (day >= 1 && day <= 31) {
        return {
          parts: { year, month, day, ...time, hasTime: Boolean(time) },
          // A missing year is a real gap, so confidence drops.
          confidence: (yearText ? 0.95 : 0.6) * (time ? 1 : 0.92),
        };
      }
    }
  }

  // May 17, 2024
  const monthDay = new RegExp(
    String.raw`\b([A-Za-z]{3,9})\.?\s+(\d{1,2})\s*(?:st|nd|rd|th)?[\s,]+(\d{4})\b`,
  ).exec(text);
  if (monthDay) {
    const month = MONTHS[(monthDay[1] ?? '').toLowerCase()];
    if (month) {
      return {
        parts: {
          year: Number(monthDay[3]),
          month,
          day: Number(monthDay[2]),
          ...time,
          hasTime: Boolean(time),
        },
        confidence: time ? 0.95 : 0.85,
      };
    }
  }

  // 17/05/2024 or 17-05-2024, day-first
  const slash = /\b(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})\b/.exec(text);
  if (slash) {
    const first = Number(slash[1]);
    const second = Number(slash[2]);
    const yearText = slash[3] ?? '';
    const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
    // Day-first unless the first number cannot be a day.
    const dayFirst = first > 12 || second <= 12;
    const day = dayFirst ? first : second;
    const month = dayFirst ? second : first;
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      return {
        parts: { year, month, day, ...time, hasTime: Boolean(time) },
        // Genuinely ambiguous (both <= 12) means we could be a month out.
        confidence: (first > 12 ? 0.95 : 0.8) * (time ? 1 : 0.92),
      };
    }
  }

  return undefined;
}

/** Turn parsed date parts into a UTC instant in the user's zone. */
export function datePartsToIso(parts: DateParts, timeZone = DEFAULT_TIME_ZONE): string {
  return zonedTimeToUtc(
    {
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour ?? 12,
      minute: parts.minute ?? 0,
      second: parts.second ?? 0,
    },
    timeZone,
  ).toISOString();
}

/** Every Ringgit amount on a line, in order. */
export function findAmounts(line: string): number[] {
  const amounts: number[] = [];
  const pattern = /(?:RM|MYR)\s*([\d,]+(?:\.\d{1,2})?)|([\d,]+\.\d{2})\b/gi;
  let match = pattern.exec(line);
  while (match) {
    const text = match[1] ?? match[2];
    if (text) {
      try {
        amounts.push(parseAmountToSen(text));
      } catch {
        // Not a readable amount; skip it rather than guess.
      }
    }
    match = pattern.exec(line);
  }
  return amounts;
}

/**
 * Strip a label ("Merchant:", "To") off the front of a value.
 *
 * The trailing word boundary matters: without it the label "to" would match
 * the first two letters of "Touch 'n Go eWallet" and return the rest of the
 * line as a merchant name.
 */
export function valueAfterLabel(line: string, labels: readonly string[]): string | undefined {
  for (const label of labels) {
    const pattern = new RegExp(
      `^\\s*${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b\\s*[:\\-]?\\s*(.+)$`,
      'i',
    );
    const match = pattern.exec(line);
    const value = match?.[1]?.trim();
    if (value) return value;
  }
  return undefined;
}

/** The line immediately after a label line, for receipts that put the value below. */
export function valueBelowLabel(
  lines: readonly string[],
  index: number,
): string | undefined {
  const next = lines[index + 1];
  return next && next.length > 0 ? next : undefined;
}

/** Combine field confidences into one score. The weakest field dominates. */
export function combineConfidence(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const min = Math.min(...values);
  const mean = values.reduce((total, value) => total + value, 0) / values.length;
  return Math.round((min * 0.6 + mean * 0.4) * 100) / 100;
}
